/*
 * Translation message keys for the onboarding plugin.
 *
 * This file defines the shape of all translatable strings.
 * When Backstage's `createTranslationRef` is adopted, these keys
 * can be passed directly as the `messages` record, making migration
 * straightforward.
 *
 * Key naming follows Backstage convention: camelCase, grouped by context.
 */

/** All translatable UI strings used by the onboarding plugin. */
export interface OnboardingMessages {
  /* ── Page chrome ────────────────────────────────── */
  rolePageTitle: string;
  rolePageSubtitle: string;
  trackPageSubtitle: string;

  /* ── Role selection page ────────────────────────── */
  roleQuestion: string;
  startAs: string; // receives {{role}}
  comingSoon: string;

  /* ── Track page ─────────────────────────────────── */
  changeRole: string;
  startJourney: string;
  reviewJourney: string;
  warningMessage: string;
  journeyLabel: string; // receives {{index}}
  stepsCount: string; // receives {{count}}
  phasesCount: string; // receives {{count}}
  journeysCount: string; // receives {{count}}
  completed: string;

  /* ── Step modal / overlay ───────────────────────── */
  back: string;
  continueLabel: string;
  finish: string;
  stepIndicator: string; // receives {{current}} / {{total}}

  /* ── Roles ──────────────────────────────────────── */
  roleDeveloperTitle: string;
  roleDeveloperDescription: string;
  roleDeveloperDuration: string;
  roleDeveloperBadge: string;
  roleDeveloperPoint1: string;
  roleDeveloperPoint2: string;
  roleDeveloperPoint3: string;

  roleTechLeadTitle: string;
  roleTechLeadDescription: string;
  roleTechLeadDuration: string;
  roleTechLeadBadge: string;
  roleTechLeadPoint1: string;
  roleTechLeadPoint2: string;
  roleTechLeadPoint3: string;
  roleTechLeadPoint4: string;

  /* ── Journey titles ─────────────────────────────── */
  journeyNavigatingTitle: string;
  journeyDeployTitle: string;
  journeyTroubleshootTitle: string;
  journeyTemplatesTitle: string;
  journeyAzureTitle: string;
  journeyApiTitle: string;
  journeyTeamTitle: string;
  journeyCostTitle: string;

  /* ── Journey descriptions ───────────────────────── */
  journeyNavigatingDesc: string;
  journeyDeployDesc: string;
  journeyTroubleshootDesc: string;
  journeyTemplatesDesc: string;
  journeyAzureDesc: string;
  journeyApiDesc: string;
  journeyTeamDesc: string;
  journeyCostDesc: string;

  /* ── Journey durations ──────────────────────────── */
  journeyNavigatingDurationDev: string;
  journeyNavigatingDurationTL: string;
  journeyDeployDuration: string;
  journeyTroubleshootDuration: string;
  journeyTemplatesDuration: string;
  journeyAzureDuration: string;
  journeyApiDuration: string;
  journeyTeamDuration: string;
  journeyCostDuration: string;

  /* ── Phase names — Navigating the IDP ───────────── */
  phaseWelcome: string;
  phaseNavigating: string;
  phaseFinding: string;
  phaseExternal: string;
  phasePermissions: string;

  /* ── Phase names — Deploy ───────────────────────── */
  phasePipeline: string;
  phaseBuild: string;
  phaseStaging: string;
  phaseProduction: string;
  phaseMonitoringRollback: string;

  /* ── Phase names — Troubleshoot ─────────────────── */
  phaseIdentifying: string;
  phaseReadingLogs: string;
  phasePipelineErrors: string;
  phaseDebugging: string;
  phaseEscalation: string;

  /* ── Phase names — Templates ────────────────────── */
  phaseTemplateGallery: string;
  phaseCreatingComponent: string;
  phaseConfiguring: string;
  phaseRepoSetup: string;
  phaseFirstDeploy: string;

  /* ── Phase names — Azure ────────────────────────── */
  phaseAzureOverview: string;
  phaseKeyVault: string;
  phaseStorage: string;
  phaseEventHub: string;
  phaseResourceMonitoring: string;

  /* ── Phase names — API ──────────────────────────── */
  phaseApiStandards: string;
  phaseScorecard: string;
  phaseRegisterApis: string;
  phaseCompliance: string;
  phaseImprovingScores: string;

  /* ── Phase names — Team ─────────────────────────── */
  phaseTeamStructure: string;
  phaseRoleAccess: string;
  phasePermissionPolicies: string;
  phaseOnboardingMembers: string;

  /* ── Phase names — Cost ─────────────────────────── */
  phaseCostDashboard: string;
  phaseUsageAnalysis: string;
  phaseOptimization: string;
  phaseBudgetAlerts: string;

  /* ── Journey step section titles ────────────────── */
  sectionWelcomePlatform: string;
  sectionNavigatingBackstage: string;
  sectionFindingProject: string;
  sectionExternalTools: string;
  sectionPermissionsSecurity: string;
  sectionBeforeStart: string;

  /* ── Journey step titles ────────────────────────── */
  stepWelcomeIdp: string;
  stepToolsAroundYou: string;
  stepHowItConnects: string;
  stepHomePage: string;
  stepQuickActions: string;
  stepSearchBar: string;
  stepOpenCatalog: string;
  stepUnderstandingKinds: string;
  stepFiltersSearch: string;
  stepFindSystem: string;
  stepSystemOverview: string;
  stepRelationsGraph: string;
  stepArgoCd: string;
  stepGrafana: string;
  stepGitHubAzureDevOps: string;
  stepPermissionsDeveloper: string;
  stepSecurityRules: string;
  stepJourneyComplete: string;
  stepTwoRepositories: string;
  stepPlatformHandles: string;

  /* ── Journey 2: Code to Deploy ──────────────────── */
  sectionCICDPipeline: string;
  stepCompleteFlow: string;
  stepPipelineStages: string;
  stepArgoCDAutoDeploy: string;
  sectionConfiguringApp: string;
  stepComponentFile: string;
  stepEnvSecrets: string;
  stepResourcesHealth: string;
  stepValidateYAML: string;
  sectionBranchesLive: string;
  stepBranchMapping: string;
  stepGoingProduction: string;
  stepIndependentlyDo: string;
  sectionMonitoringDeploys: string;
  stepCheckingArgoCD: string;
  stepCheckingLogs: string;

  /* ── Journey 3: When Something Goes Wrong ────────── */
  sectionJ3CheckLogs: string;
  stepJ3StartGrafana: string;
  stepJ3WhatToLookLogs: string;
  sectionJ3CheckDeploy: string;
  stepJ3WhenNotInLogs: string;
  stepJ3GitOpsMisconfiguration: string;
  stepJ3PipelineFailures: string;
  stepJ3ImageNotUpdating: string;
  sectionJ3AskHelp: string;
  stepJ3OpenTicket: string;
  stepJ3Journey3Complete: string;

  /* ── Quiz ────────────────────────────────────────── */
  sectionQuiz: string;
  stepQuiz: string;
  quizQuestionOf: string; // receives {{current}} / {{total}}
  quizConfirm: string;
  quizCorrectAnswer: string; // receives {{letter}}
  quizPassed: string;
  quizFailed: string;
  quizCorrectAnswers: string;
  quizNextJourneyUnlocked: string;
  quizNextSteps: string;
  quizContinueToJourney: string;
  quizFinishOnboarding: string;
  quizTryAgain: string;
  quizStartQuiz: string;
  quizFailedMessage: string;
  quizReviewJourney: string;

  /* ── Playground ───────────────────────────────────── */
  playgroundGoToPlayground: string;
  sectionPlayground: string;
  stepPlayground: string;
  playgroundTitle: string;
  playgroundDescription: string;
  playgroundTaskListHeader: string;
  playgroundTask1: string;
  playgroundTask2: string;
  playgroundTask3: string;
  playgroundTask4: string;
  playgroundTask5: string;
  playgroundTutorialLabel: string;
  playgroundTutorialDescription: string;
  playgroundRepoLabel: string;
  playgroundVerifyHint: string;
  playgroundVerify: string;
  playgroundCompleted: string;
  playgroundCompletedMessage: string;
  playgroundNextJourneyUnlocked: string;
  playgroundContinueToJourney: string;
  playgroundFailedTitle: string;
  playgroundFailedMessage: string;
  playgroundFailedHint: string;
  playgroundReviewJourney: string;
  playgroundVerifyAgain: string;

  /* ── Exit confirmation ───────────────────────────── */
  exitConfirmTitle: string;
  exitConfirmMessage: string;
  exitConfirmLeave: string;
  exitConfirmStay: string;

  /* ── Language selector ──────────────────────────── */
  languageLabel: string;

  /* ── Prerequisites ──────────────────────────────── */
  prerequisitesTitle: string;
  prerequisitesDescription: string;
  prerequisiteGithubLicense: string;
  prerequisiteGithubCopilot: string;
  prerequisiteGithubCodespaces: string;
  prerequisitesAlignWithTechLead: string;
  prerequisitesRequestAccess: string;
  prerequisitesHowToFill: string;

  /* ── NPS ─────────────────────────────────────────── */
  npsTitle: string;
  npsDescription: string;
  npsQuestionsCount: string; // receives {{count}}
  npsBadge: string;
  npsStart: string;
  npsSubmitError: string;
  reviewNps: string;
  npsThankYouSubtitle: string;
}

export type OnboardingMessageKey = keyof OnboardingMessages;

/** Simple interpolation: replaces {{key}} tokens in a message string. */
export function interpolate(
  template: string,
  values: Record<string, string | number>,
): string {
  return Object.entries(values).reduce(
    (result, [key, value]) =>
      result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), String(value)),
    template,
  );
}
